import {Form, Select, Table, Tag} from 'antd';
import {useEffect, useState} from 'react';
import styles from './allRewardSchemes.module.scss';
import {useMutation, useQuery} from '@apollo/client';
import {toast} from 'react-toastify';
import {useParams} from 'react-router-dom';
import {GET_USER_REWARDS} from 'graphql/queries/rewardScheme';
import DBModal from 'components/modal/modal';
import UserLink from 'components/userLink/UserLink';
import {rewardStatus} from 'utils/mock';
import {
  assetClassLabel,
  instrumentKeyColor,
  instrumentKeyLabel,
} from 'pages/AssetManagement/modules/instrumentKeys';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {UPDATE_USER_REWARD_STATUS} from 'graphql/mutations/rewardScheme';
import moment from 'moment';
import gamer from 'images/gamer.png';

const UserReward = () => {
  const {id} = useParams();
  const [allUserRewards, setAllUserRewards] = useState([]);
  const [openStatusModal, setStatusModal] = useState(false);
  const [userReward, setUserReward] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const {loading, refetch, data} = useQuery(GET_USER_REWARDS, {
    variables: {
      rewardSchemeId: id,
      page: 1,
      limit: 10,
    },
    onCompleted: data => setAllUserRewards(data?.adminGetAllUserRewards?.data),
    errorPolicy: 'all',
    onError: error => toast.error(error?.message),
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });

  const [adminUpdateUserRewardStatus] = useMutation(UPDATE_USER_REWARD_STATUS);

  useEffect(() => {
    setLimit(10);
  }, []);

  const handlePageChange = (page, pageSize) => {
    if (page === currentPage && pageSize === limit) return;
    setCurrentPage(page);
    setLimit(pageSize);
    refetch({
      page,
      limit: pageSize,
      rewardSchemeId: id,
    });
  };

  const handleSave = formValues => {
    adminUpdateUserRewardStatus({
      variables: {
        adminUpdateUserRewardStatusId: userReward,
        status: formValues?.status,
      },
      refetchQueries: [{query: GET_USER_REWARDS}],
    }).then(({data: {adminUpdateUserRewardStatus}}) => {
      if (adminUpdateUserRewardStatus.__typename === 'Error') {
        toast.error(adminUpdateUserRewardStatus.message);
      } else {
        setStatusModal(false);
        refetch();
        toast.success('User reward updated successfully');
      }
    });
  };

  const columns = [
    {
      title: 'User',
      dataIndex: 'user',
      key: 'user',
      render: (text, record) => (
        <div className={styles['record-label']}>
          {record?.userType === 'USER' ? (
            <div className={styles['record-name']}>
              {record?.userId?.imageUrl ? (
                <img src={record?.userId?.imageUrl} alt="" />
              ) : (
                <img src={gamer} alt="" />
              )}
              <h4>
                <UserLink userId={record?.userId?.id}>
                  {record?.userId?.firstName} {record?.userId?.lastName}
                </UserLink>
              </h4>
            </div>
          ) : record?.userType === 'REFEREE' ? (
            <div className={styles['record-name']}>
              {record?.refereeId?.imageUrl ? (
                <img src={record?.refereeId?.imageUrl} alt="" />
              ) : (
                <img src={gamer} alt="" />
              )}
              <h4>
                <UserLink userId={record?.refereeId?.id}>
                  {record?.refereeId?.firstName} {record?.refereeId?.lastName}
                </UserLink>
              </h4>
            </div>
          ) : (
            <div className={styles['record-name']}>
              {record?.referrerId?.imageUrl ? (
                <img src={record?.referrerId?.imageUrl} alt="" />
              ) : (
                <img src={gamer} alt="" />
              )}
              <h4>
                <UserLink userId={record?.referrerId?.id}>
                  {record?.referrerId?.firstName} {record?.referrerId?.lastName}
                </UserLink>
              </h4>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Reward Type',
      dataIndex: 'rewardType',
      key: 'rewardType',
      render: (text, record) => (
        <div>
          <h4>{record?.userReward?.type.replaceAll('_', ' ')}</h4>
        </div>
      ),
    },
    {
      title: 'Reward',
      dataIndex: 'reward',
      key: 'reward',
      render: (text, record) => (
        <div>
          <h4>
            {record?.userReward?.type === 'TRANSACTION_FEE_WAIVER'
              ? `${record?.userReward?.cashValue} ${record?.userReward?.currency?.currency}`
              : record?.userReward?.type === 'STOCKS'
              ? // A ticker alone is no longer unique — the same symbol can
                // list on two exchanges — so name the exchange when present.
                `${record?.userReward?.rewardUnits} units of ${
                  record?.userReward?.ticker
                    ? `${record.userReward.ticker}${
                        record.userReward.exchangeCode
                          ? ` · ${record.userReward.exchangeCode}`
                          : ''
                      }`
                    : 'Any'
                }`
              : `${record?.userReward?.amount} ${record?.userReward?.currency?.currency}`}
          </h4>
        </div>
      ),
    },
    {
      title: 'User Type',
      dataIndex: 'userType',
      key: 'userType',
      render: (text, render) => (
        <div className={styles['record-label']}>
          <h4>{render?.userType}</h4>
        </div>
      ),
    },
    {
      title: 'Action Type',
      dataIndex: 'eventType',
      key: 'eventType',
      render: (text, render) => (
        <div>
          <Tag>{render?.eventType?.replaceAll('_', ' ')}</Tag>
        </div>
      ),
    },
    {
      // Which asset the event was on, at both levels of the scheme's scope —
      // the difference between a reward that paid on a listed stock and one
      // that paid on an IPO allocation is only visible here.
      title: 'Asset',
      dataIndex: 'eventInstrumentKey',
      key: 'eventInstrumentKey',
      render: (text, render) =>
        render?.eventAssetClassKey || render?.eventInstrumentKey ? (
          <div className={styles['record-label']}>
            <h4>{assetClassLabel(render?.eventAssetClassKey)}</h4>
            {render?.eventInstrumentKey && (
              <Tag color={instrumentKeyColor(render?.eventInstrumentKey)}>
                {instrumentKeyLabel(render?.eventInstrumentKey)}
              </Tag>
            )}
          </div>
        ) : (
          <div className={styles['record-label']}>
            <h4>--</h4>
          </div>
        ),
    },
    {
      title: 'Amount',
      dataIndex: 'eventAmount',
      key: 'eventAmount',
      render: (text, render) => (
        <div className={styles['record-label']}>
          <h4>
            {render?.eventCurrency?.currency} {render?.eventAmount}
          </h4>
        </div>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text, render) => (
        <div className={styles['record-label']}>
          <h4>{moment(render?.createdAt).format('DD MMMM, YYYY')}</h4>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (text, record) => (
        <div className={styles['record-label']}>
          {record?.status === 'PENDING' ? (
            <Tag color="yellow">Pending</Tag>
          ) : record?.status === 'REJECTED' ? (
            <Tag color="red">Rejected</Tag>
          ) : (
            <Tag color="green">Approved</Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Actions',
      dataIndex: 'operation',
      key: 'actions',
      render: (text, record) => (
        <div className={styles.actions}>
          {record?.status === 'PENDING' && (
            <>
              <div
                onClick={() => {
                  setStatusModal(true);
                  setUserReward(record?.id);
                }}
                className={styles['action-icon']}>
                <span>Update Status</span>
              </div>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className={styles['table-container']}>
      <div className={styles['startup-table']}>
        <Table
          className={styles['user-table']}
          dataSource={allUserRewards}
          loading={loading}
          columns={columns}
          pagination={{
            current: currentPage,
            onChange: handlePageChange,
            total: data?.adminGetAllUserRewards?.pagination?.total,
            showQuickJumper: true,
            className: 'ant-table-pagination',
          }}
        />
      </div>

      <DBModal
        isOpen={openStatusModal}
        handleClose={() => setStatusModal(false)}
        content={
          <div>
            <h1>Update Status</h1>
            <Form
              className={styles.form}
              layout="vertical"
              onFinish={handleSave}>
              <div className={styles['date-format']}>
                <Form.Item
                  rules={[
                    {
                      required: true,
                      message: 'Status required',
                    },
                  ]}
                  name="status"
                  label="Status">
                  <Select style={{width: 340}} name="status">
                    {rewardStatus.map(({key, name}) => (
                      <Select.Option value={key} key={key}>
                        {name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>
              <SubmitButton label="Update" />
            </Form>
          </div>
        }
      />
    </div>
  );
};

export {UserReward};
